import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ExternalLink } from "lucide-react";
import { http, formatErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("ghp_admin_token") || ""}` } });

export const RecipesAdmin = () => {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/recipes", auth());
      setItems(Array.isArray(data?.recipes) ? data.recipes : []);
    } catch (e) {
      toast.error(formatErr(e));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const count = items.filter((r) => r.featured).length;
  const toggle = (slug) => setItems((list) => list.map((r) => (r.slug === slug ? { ...r, featured: !r.featured } : r)));
  const save = async () => {
    setSaving(true);
    try {
      await http.put("/admin/recipes/featured", { slugs: items.filter((r) => r.featured).map((r) => r.slug) }, auth());
      toast.success("Featured recipes updated");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
    setSaving(false);
  };

  return (
    <div data-testid="admin-recipes">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-4 rounded-[22px] border border-[#E3D9CD] bg-white p-5">
        <p className="text-sm text-[#4A4543]">Turned-on recipes show on the homepage with their own share page. The rest stay exclusive to buyers in the PDF. <strong data-testid="admin-recipes-count">{count} of 12 featured</strong></p>
        <button data-testid="admin-recipes-save-button" disabled={saving || count === 0} onClick={save} className="flex h-11 items-center gap-2 rounded-full bg-[#C85A32] px-6 text-sm font-semibold text-[#FDFBF7] hover:bg-[#A84422] disabled:opacity-50">
          {saving && <Loader2 size={16} className="animate-spin" />} Save featured
        </button>
      </div>
      {count === 0 && <p className="mb-4 text-sm text-red-700">Pick at least one recipe.</p>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((r) => (
          <div key={r.slug} data-testid={`admin-recipe-${r.slug}`} className={`overflow-hidden rounded-[22px] border bg-white transition-[border-color] ${r.featured ? "border-[#C85A32]" : "border-[#E3D9CD]"}`}>
            <img src={r.image} alt={r.name} className="aspect-[1200/630] w-full object-cover" />
            <div className="flex items-start justify-between gap-3 p-4">
              <div>
                <p className="eyebrow text-[0.58rem] text-[#6E6661]">{r.chapter}</p>
                <p className="font-display mt-1 text-lg">{r.name}</p>
                {r.featured && <a href={`/recipes/${r.slug}`} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-[#C85A32]">View page <ExternalLink size={11} /></a>}
              </div>
              <Switch data-testid={`admin-recipe-toggle-${r.slug}`} checked={!!r.featured} onCheckedChange={() => toggle(r.slug)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AccountAdmin = () => {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next !== confirm) {
      setErr("New passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await http.post("/auth/change-password", { current_password: current, new_password: next }, auth());
      toast.success("Password updated — use it next time you sign in");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e2) {
      setErr(formatErr(e2));
    }
    setLoading(false);
  };
  return (
    <form onSubmit={submit} data-testid="change-password-form" className="max-w-md rounded-[22px] border border-[#E3D9CD] bg-white p-7">
      <h2 className="font-display text-2xl font-light italic">Change password</h2>
      <p className="mt-1 text-sm text-[#6E6661]">At least 10 characters, with letters and numbers.</p>
      <Input data-testid="current-password-input" type="password" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-5 h-12 rounded-xl" required autoComplete="current-password" />
      <Input data-testid="new-password-input" type="password" placeholder="New password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-3 h-12 rounded-xl" required minLength={10} autoComplete="new-password" />
      <Input data-testid="confirm-password-input" type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-3 h-12 rounded-xl" required autoComplete="new-password" />
      {err && <p data-testid="change-password-error" className="mt-3 text-sm text-red-700">{err}</p>}
      <button data-testid="change-password-submit" disabled={loading} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#181615] py-3.5 text-sm font-semibold text-[#FDFBF7] hover:bg-[#C85A32]">
        {loading && <Loader2 size={16} className="animate-spin" />} Update password
      </button>
    </form>
  );
};
